import { get, post, put, del } from '../request';
import { toPage, type Page, type PageQuery } from './types';

/** 公告中心 API（管理端「系统 → 公告管理」+ 坐席端强制过关 Gate） */

export type NoticeStatus = 'draft' | 'published' | 'offline';
export type QuestionType = 'single' | 'blank';

export interface Notice {
  id: number;
  title: string;
  content: string;
  status: NoticeStatus;
  /** 生效时间窗（RFC3339 / YYYY-MM-DD HH:mm:ss；null=无界） */
  start_at?: string | null;
  end_at?: string | null;
  /** 目标角色，逗号分隔；空=仅 agent（坐席） */
  target_roles?: string;
  /** false=仅阅读确认（不答题） */
  require_quiz: boolean;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
}

export interface NoticeQuestion {
  id: number;
  notice_id: number;
  type: QuestionType;
  stem: string;
  /** JSON [{key,text}]；填空题为空 */
  options?: string;
  /** JSON 答案数组（单选为键数组、填空为别名数组） */
  answer?: string;
  sort?: number;
}

export interface QuestionOption {
  key: string;
  text: string;
}

export interface NoticeStats {
  target: number;
  passed: number;
  pending: number;
  expired: number;
}

/** 下发坐席的题目（不含答案） */
export interface PublicQuestion {
  id: number;
  type: QuestionType;
  stem: string;
  options?: string;
  sort?: number;
  /** 填空空数（= 答案组数），坐席端据此渲染内联输入框 */
  blank_count?: number;
}

export interface PendingNotice {
  notice: Notice;
  questions: PublicQuestion[];
}

export interface AnswerInput {
  question_id: number;
  value: string[];
}

export interface AnswerDetail {
  question_id: number;
  correct: boolean;
  answer: string[];
}

export interface SubmitResult {
  passed: boolean;
  details: AnswerDetail[];
}

/** 公告状态 → 文案/色（管理页状态列与统计共用，禁止页面各写一份） */
const NOTICE_STATUS_META: Record<string, [string, 'gray' | 'green']> = {
  draft: ['草稿', 'gray'],
  published: ['已发布', 'green'],
  offline: ['已下线', 'gray'],
};

export function noticeStatusMeta(status?: string): [string, 'gray' | 'green'] {
  return NOTICE_STATUS_META[status || ''] || [status || '-', 'gray'];
}

/** 题型 → 中文 */
export function questionTypeLabel(t?: string): string {
  if (t === 'single') return '单选';
  if (t === 'blank') return '填空';
  return t || '-';
}

/** 解析选项 JSON（非法返回空数组） */
export function parseOptions(raw?: string): QuestionOption[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/** 解析答案 JSON 数组（非法返回空数组） */
export function parseAnswerArray(raw?: string): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.map((v) => String(v)) : [];
  } catch {
    return [];
  }
}

/**
 * 解析填空答案为按空分组（每空一组可接受写法）。
 * 兼容旧扁平格式 ["a","b"]（单空多别名）→ [["a","b"]]。
 */
export function parseAnswerGroups(raw?: string): string[][] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return [];
    if (Array.isArray(arr[0])) {
      return arr.map((g) => (Array.isArray(g) ? g.map((x) => String(x)) : []));
    }
    return [arr.map((x) => String(x))];
  } catch {
    return [];
  }
}

// ---------- 管理端 ----------

/** GET /notices */
export async function listNotices(q: PageQuery & { status?: string }): Promise<Page<Notice>> {
  return toPage<Notice>(await get('/notices', q));
}

/** POST /notices */
export function createNotice(v: Partial<Notice>) {
  return post('/notices', v);
}

/** GET /notices/:id（含题目与答案） */
export function getNotice(id: number): Promise<{ notice: Notice; questions: NoticeQuestion[] }> {
  return get(`/notices/${id}`);
}

/** PUT /notices/:id（已发布公告改动会清空回执，坐席需重新作答） */
export function updateNotice(id: number, v: Partial<Notice>) {
  return put(`/notices/${id}`, v);
}

/** DELETE /notices/:id */
export function deleteNotice(id: number) {
  return del(`/notices/${id}`);
}

/** POST /notices/:id/publish（expirePrevious=true 时同时让其它生效中公告失效） */
export function publishNotice(id: number, expirePrevious = false) {
  return post(`/notices/${id}/publish`, { expire_previous: expirePrevious });
}

/** POST /notices/:id/offline */
export function offlineNotice(id: number) {
  return post(`/notices/${id}/offline`, {});
}

/** GET /notices/:id/stats */
export function getNoticeStats(id: number): Promise<NoticeStats> {
  return get(`/notices/${id}/stats`);
}

/** POST /notices/:id/questions */
export function createNoticeQuestion(noticeId: number, v: Partial<NoticeQuestion>) {
  return post(`/notices/${noticeId}/questions`, v);
}

/** PUT /notices/questions/:qid */
export function updateNoticeQuestion(qid: number, v: Partial<NoticeQuestion>) {
  return put(`/notices/questions/${qid}`, v);
}

/** DELETE /notices/questions/:qid */
export function deleteNoticeQuestion(qid: number) {
  return del(`/notices/questions/${qid}`);
}

// ---------- 坐席端 ----------

/** GET /notices/pending（本人待完成公告，不含答案） */
export function fetchPendingNotices(): Promise<PendingNotice[]> {
  return get('/notices/pending');
}

/** POST /notices/:id/submit */
export function submitNotice(id: number, answers: AnswerInput[]): Promise<SubmitResult> {
  return post(`/notices/${id}/submit`, { answers });
}

/** POST /notices/:id/read */
export function markNoticeRead(id: number) {
  return post(`/notices/${id}/read`, {});
}

/** GET /notices/unread-count（当前用户未完成的生效中公告数，供通知铃角标） */
export async function fetchNoticeUnreadCount(): Promise<number> {
  const res = await get<{ unread: number }>('/notices/unread-count');
  return res?.unread ?? 0;
}

/** 我的公告历史筛选档（GET /notices/mine 的 status） */
export type MyNoticeHistoryStatus = 'passed' | 'expired' | 'all';

/** 我的公告历史一条：公告本体 + 本人回执摘要（receipt_status 空 = 本人没有任何回执，只在已失效档出现） */
export interface MyNoticeHistoryItem {
  notice: Notice;
  receipt_status: string;
  passed_at?: string | null;
  read_at?: string | null;
  attempts: number;
}

/** 我的公告历史档 → 中文（通知铃与消息中心共用，禁止两处各写一份） */
export const MY_NOTICE_HISTORY_LABELS: Record<MyNoticeHistoryStatus, string> = {
  passed: '已完成',
  expired: '已失效',
  all: '全部',
};

/** GET /notices/mine（本人公告历史：已完成 / 已失效未完成 / 全部，服务端分页） */
export async function fetchMyNoticeHistory(
  q: { status?: MyNoticeHistoryStatus; page?: number; pageSize?: number }
): Promise<Page<MyNoticeHistoryItem>> {
  return toPage<MyNoticeHistoryItem>(
    await get('/notices/mine', { status: q.status || 'passed', page: q.page ?? 1, pageSize: q.pageSize ?? 20 })
  );
}
